import React, { useState, useEffect, useRef } from "react";

import { CSVLink } from "react-csv";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileDownload, faPrint } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import _ from "lodash";
import dayjs from "dayjs";
import Select from "react-select";
import { Card, Row, Col, Input, Button, Table, DatePicker, Tooltip, Tag, Space } from "antd";
import ReactToPrint from "react-to-print";
import WindowedSelect from "react-windowed-select";

function InvoiceReports(props) {
  //filter options
  const [users, setUsers] = useState([]);
  const [clientCategories, setClientCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [itemCategories, setItemCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [groupings, setGroupings] = useState([{ label: "User", value: "user_name" }]);

  //filter
  const [invoiceReportFilters, setInvoiceReportFilters] = useState({
    daterange_start: dayjs().subtract(1, "month").startOf("month"),
    daterange_end: dayjs().subtract(1, "month").endOf("month"),
    users: [],
    client_categories: [],
    clients: [],
    item_categories: [],
    items: [],
    billing_status_categories: [],
    billing_statuses: [],
    partners: [],
    managers: [],
  });

  const [invoices, setInvoices] = useState([]);
  const [flatInvoices, setFlatInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [invoiceSearchTerm, setinvoiceSearchTerm] = useState("");

  const printableRef = useRef();

  const invoice_report_columns = [
    {
      title: "Name",
      dataIndex: "invoice_name",
      key: "invoice_name",
      render: (value, row) =>
        row.special ? (
          <b>{value}</b>
        ) : (
          <Link to={"/invoicing/" + row.tag}>
            <Tag>#{value}</Tag>
          </Link>
        ),
      sorter: (a, b) => (a?.invoice_name?.toUpperCase() > b?.invoice_name?.toUpperCase() ? 1 : -1),
    },
    {
      title: "User",
      dataIndex: "user_name",
      key: "user_name",
      sorter: (a, b) => (a?.user_name?.toUpperCase() > b?.user_name?.toUpperCase() ? 1 : -1),
    },
    {
      title: "Client",
      dataIndex: "client_name",
      key: "client_name",
      render: (value, row) => <Link to={"/client-details/" + row.client_tag}>{value}</Link>,
      sorter: (a, b) => (a?.client_name?.toUpperCase() > b?.client_name?.toUpperCase() ? 1 : -1),
    },
    {
      title: "Date",
      dataIndex: "invoiced_at",
      key: "invoiced_at",
      render: (value) => props.MakeDateTimeReadable(value),
      sorter: (a, b) => (a.invoiced_at > b.invoiced_at ? 1 : -1),
    },
    {
      title: "Billed Time Amount",
      dataIndex: "time_total",
      key: "time_total",
      align: "right",
      render: (value) => props.FormatNumberToCurrency(value),
      sorter: (a, b) => (a.time_total > b.time_total ? 1 : -1),
    },
    {
      title: "Adjustments Amount",
      dataIndex: "adjustment_total",
      key: "adjustment_total",
      align: "right",
      render: (value) => props.FormatNumberToCurrency(value),
      sorter: (a, b) => (a.adjustment_total > b.adjustment_total ? 1 : -1),
    },
    {
      title: "Invoice Amount",
      dataIndex: "line_item_total",
      key: "line_item_total",
      align: "right",
      render: (value) => props.FormatNumberToCurrency(value),
      sorter: (a, b) => (a.line_item_total > b.line_item_total ? 1 : -1),
    },
    {
      title: "Percent of Standard Bill Rate",
      dataIndex: "line_item_total",
      key: "line_item_total_1",
      align: "right",
      render: (value, row) => (row.special ? props.FormatNumberTwoDecimals((row.line_item_total / row.time_total) * 100.0) + "%" : null),
      // sorter: (a, b) => (a.special && b.special ? (a.line_item_total / a.total_time > b.line_item_total / b.total_time ? 1 : -1) : false),
    },
  ];

  const GetUsers = async () => {
    let response = await props.AxiosRequest("/api/users/get");
    setUsers(
      response.data.users.map((m) => {
        return { ...m, value: m.id, label: m.name };
      }),
    );
  };
  const GetClientCategories = async () => {
    let response = await props.AxiosRequest("/api/client-categories/get");
    setClientCategories(
      response.data.client_categories.map((m) => {
        return { ...m, value: m.id, label: m.name };
      }),
    );
  };
  const GetClients = async () => {
    let response = await props.AxiosRequest("/api/clients/get/all");
    setClients(
      response.data.clients.map((m) => {
        return { ...m, value: m.id, label: m.name };
      }),
    );
  };
  const GetItemCategories = async () => {
    let response = await props.AxiosRequest("/api/item-categories/get");
    setItemCategories(
      response.data.item_categories.map((m) => {
        return { ...m, value: m.id, label: m.name };
      }),
    );
  };
  const GetItems = async () => {
    let response = await props.AxiosRequest("/api/items/get");
    setItems(
      response.data.items.map((m) => {
        return { ...m, value: m.id, label: m.name };
      }),
    );
  };

  const GetInvoices = async () => {
    let response = await props.AxiosRequest("/api/invoices/get/reporting", invoiceReportFilters);
    let grouped_data = await GroupInvoiceData(response.data.data);
    setInvoices(grouped_data);
    setFlatInvoices(response.data.data);
  };
  const GroupInvoiceData = async (data) => {
    if (groupings.length === 0) return data;
    let grouped_data = _.groupBy(data, (result) => groupings.reduce((a, b) => a + result[b.value] + ", ", "").slice(0, -2));
    let grouped_data_array = _.keys(grouped_data).map((m) => {
      return {
        invoice_name: m,
        special: true,
        key: "group-" + m,
        children: grouped_data[m],
        time_total: props.FormatNumberTwoDecimals(grouped_data[m].reduce((a, b) => a + b.time_total, 0)),
        adjustment_total: props.FormatNumberTwoDecimals(grouped_data[m].reduce((a, b) => a + b.adjustment_total, 0)),
        line_item_total: props.FormatNumberTwoDecimals(grouped_data[m].reduce((a, b) => a + b.adjustment_total + b.time_total, 0)),
      };
    });

    return grouped_data_array;
  };
  const FilterInvoices = async () => {
    if (invoiceSearchTerm?.length === 0) return setFilteredInvoices(invoices);
    let filtered_list = invoices.filter((f) =>
      _.toLower(f.client_name).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.job_name).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.description).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.created_by).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.item_name).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.time_at).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.hours).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.comments).indexOf(_.toLower(invoiceSearchTerm)) > -1 ||
      _.toLower(f.billing_status_name).indexOf(_.toLower(invoiceSearchTerm)) > -1
        ? true
        : false,
    );
    setFilteredInvoices(filtered_list);
  };

  useEffect(() => {
    GetUsers();
    GetClientCategories();
    GetClients();
    GetItemCategories();
    GetItems();
    GetInvoices();
  }, []);

  useEffect(() => {
    FilterInvoices();
  }, [invoices, invoiceSearchTerm]);

  return (
    <div className="container-fluid">
      <div className="header">
        <h1 className="header-title">Invoice Reports</h1>
      </div>

      <Card
        title={
          <Row gutter={[16, 16]}>
            <Col span={12}>Invoice Report Filters</Col>
            {/* <Col span={12} className="text-right">
              <FontAwesomeIcon icon={faQuestionCircle} size="small" />
            </Col> */}
          </Row>
        }
      >
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <label className="m-1">Date Range</label>
            <DatePicker.RangePicker
              className="w-100"
              allowClear={false}
              format="MM/DD/YYYY"
              presets={props.default_rangepicker_presets}
              value={[invoiceReportFilters.daterange_start, invoiceReportFilters.daterange_end]}
              onCalendarChange={(dates) => {
                setInvoiceReportFilters({ ...invoiceReportFilters, daterange_start: dates[0], daterange_end: dates[1] });
              }}
            />
          </Col>
          <Col span={6}>
            <label className="m-1">User</label>
            <Select
              isMulti
              options={props.MakeUserOptions(users)}
              value={invoiceReportFilters.users}
              onChange={(e) => {
                setInvoiceReportFilters({ ...invoiceReportFilters, users: e });
              }}
              placeholder="All Users"
            />
          </Col>
          <Col span={6}>
            <label className="m-1">Client Categories</label>
            <Select
              isMulti
              options={clientCategories}
              value={invoiceReportFilters.client_categories}
              onChange={(e) => {
                setInvoiceReportFilters({ ...invoiceReportFilters, client_categories: e });
              }}
              placeholder="All Client Categories"
            />
          </Col>
          <Col span={6}>
            <label className="m-1">Clients</label>
            <WindowedSelect
              isMulti
              options={props.MakeClientOptions(clients)}
              value={invoiceReportFilters.clients}
              onChange={(e) => {
                setInvoiceReportFilters({ ...invoiceReportFilters, clients: e });
              }}
              placeholder="All Clients"
            />
          </Col>
          <Col span={6}>
            <label className="m-1">Item Categories</label>
            <Select
              isMulti
              options={itemCategories}
              value={invoiceReportFilters.item_categories}
              onChange={(e) => {
                setInvoiceReportFilters({ ...invoiceReportFilters, item_categories: e });
              }}
              placeholder="All Item Categories"
            />
          </Col>
          <Col span={6}>
            <label className="m-1">Items</label>
            <Select
              isMulti
              options={items}
              value={invoiceReportFilters.items}
              onChange={(e) => {
                setInvoiceReportFilters({ ...invoiceReportFilters, items: e });
              }}
              placeholder="All Items"
            />
          </Col>
          <Col span={24}>
            <hr />
          </Col>
          <Col span={8}>
            <label className="m-1">Groupings</label>
            <Select
              isMulti
              options={[
                { label: "User", value: "user_name" },
                // { label: "Client Category", value: "client_categories_name" },
                { label: "Client", value: "client_name" },
                { label: "Item Category", value: "item_category_name" },
                { label: "Item", value: "item_name" },
                { label: "Invoice", value: "invoice_name" },
              ]}
              placeholder="Grouped By"
              value={groupings}
              onChange={(e) => {
                setGroupings(e);
              }}
            />
          </Col>
          <Col span={16} className="text-right">
            <label className="m-1 w-100">&nbsp;</label>
            <Button type="primary" onClick={GetInvoices} style={{ display: "inline-block", alignSelf: "flex-end" }}>
              Submit
            </Button>
          </Col>
        </Row>
      </Card>
      <br />
      <Card
        title={
          <Row gutter={[16, 16]}>
            <Col span={12}>Invoice Report Data</Col>
          </Row>
        }
        ref={printableRef}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Table
              pagination={false}
              columns={invoice_report_columns}
              dataSource={filteredInvoices}
              summary={(pageData) => {
                let total_billed = 0;
                let total_adjustments = 0;
                let total_invoiced = 0;
                pageData.forEach(({ time_total, adjustment_total, line_item_total }) => {
                  total_billed += parseFloat(time_total);
                  total_adjustments += parseFloat(adjustment_total);
                  total_invoiced += parseFloat(line_item_total);
                });
                return (
                  <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>Summary</Table.Summary.Cell>
                      <Table.Summary.Cell index={1} />
                      <Table.Summary.Cell index={2} />
                      <Table.Summary.Cell index={3} />
                      <Table.Summary.Cell index={4} align="right">
                        {props.FormatNumberToCurrency(total_billed)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">
                        {props.FormatNumberToCurrency(total_adjustments)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">
                        {props.FormatNumberToCurrency(total_invoiced)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">
                        {props.FormatNumberTwoDecimals((total_invoiced / total_billed) * 100) + "%"}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>
                );
              }}
            />
          </Col>
          <Col span={24} className="text-right no-print">
            <Space>
              <CSVLink filename="InvoiceReport.csv" data={flatInvoices.map(({ children, key, special, ...keepAttrs }) => keepAttrs)}>
                <Button type="primary" size="small">
                  <FontAwesomeIcon icon={faFileDownload} className="mr-2" /> Download Detailed CSV File
                </Button>
              </CSVLink>
              <CSVLink filename="InvoiceReport.csv" data={filteredInvoices.map(({ children, key, special, ...keepAttrs }) => keepAttrs)}>
                <Button size="small">
                  <FontAwesomeIcon icon={faFileDownload} className="mr-2" /> Download Summary CSV File
                </Button>
              </CSVLink>
              <ReactToPrint
                trigger={() => (
                  <Button size="small">
                    <FontAwesomeIcon icon={faPrint} className="mr-2" /> Print Report
                  </Button>
                )}
                content={() => printableRef.current}
              />
            </Space>
            {/* <CSVLink filename="InvoiceReport.csv" data={filteredInvoices.map(({ children, key, special, ...keepAttrs }) => keepAttrs)}>
              <Button type="primary">
                <FontAwesomeIcon icon={faFileDownload} className="mr-2" /> Download CSV File
              </Button>
            </CSVLink> */}
          </Col>
        </Row>
      </Card>
    </div>
  );
}

export default InvoiceReports;
